// Lightweight i18n primitive.
//
// Scope for this iteration: translate the Login page copy (and any string
// that calls `t()`). The choice is persisted in localStorage under
// `iot-hub-locale` and made available app-wide via `useLocale()`.
//
// Design goals:
//   * Zero runtime dependencies.
//   * Additive — untranslated pages just render English (the source key),
//     so partial rollout is safe.
//   * Extensible — add a new locale by dropping keys into `dict` below.
//
// Future work: expand dictionaries to the whole app (Dashboard, Sidebar,
// etc.) and wire an app-header locale switcher for signed-in users.

import { useSyncExternalStore } from "react";

export const LOCALES = [
  { code: "en", label: "English",  short: "EN", flag: "🇬🇧" },
  { code: "es", label: "Español",  short: "ES", flag: "🇪🇸" },
  { code: "fr", label: "Français", short: "FR", flag: "🇫🇷" },
  { code: "pt", label: "Português", short: "PT", flag: "🇧🇷" },
];

const STORAGE_KEY = "iot-hub-locale";

// English keys double as fallbacks — when a locale is missing a key we
// return the key itself, which reads fine because we key on the English copy.
const dict = {
  es: {
    "Sign in": "Iniciar sesión",
    "Use your operator username & password. Sessions last 4 hours.":
      "Introduce tu usuario y contraseña. Las sesiones duran 4 horas.",
    "Username": "Usuario",
    "Password": "Contraseña",
    "Sign in →": "Iniciar sesión →",
    "Signing in…": "Iniciando sesión…",
    "Sessions stored locally · sent as {{header}} on every request.":
      "Sesión guardada localmente · enviada como {{header}} en cada petición.",
    "Username and password are required": "Usuario y contraseña son obligatorios",
    "Invalid credentials": "Credenciales inválidas",
    "Welcome, {{name}}": "Bienvenido, {{name}}",
    "What's new": "Novedades",
    "build": "build",
    "region": "región",
    "env": "entorno",
    "Previous highlight": "Anterior",
    "Next highlight": "Siguiente",
    "new": "nuevo",
    "improved": "mejorado",
    "update": "actualización",
  },
  fr: {
    "Sign in": "Connexion",
    "Use your operator username & password. Sessions last 4 hours.":
      "Utilisez votre identifiant et mot de passe. Sessions valables 4 heures.",
    "Username": "Identifiant",
    "Password": "Mot de passe",
    "Sign in →": "Se connecter →",
    "Signing in…": "Connexion…",
    "Sessions stored locally · sent as {{header}} on every request.":
      "Session stockée localement · envoyée en tant que {{header}} à chaque requête.",
    "Username and password are required": "Identifiant et mot de passe requis",
    "Invalid credentials": "Identifiants invalides",
    "Welcome, {{name}}": "Bienvenue, {{name}}",
    "What's new": "Nouveautés",
    "build": "build",
    "region": "région",
    "env": "environnement",
    "Previous highlight": "Précédent",
    "Next highlight": "Suivant",
    "new": "nouveau",
    "improved": "amélioré",
    "update": "mise à jour",
  },
  pt: {
    "Sign in": "Entrar",
    "Use your operator username & password. Sessions last 4 hours.":
      "Use seu usuário e senha. Sessões duram 4 horas.",
    "Username": "Usuário",
    "Password": "Senha",
    "Sign in →": "Entrar →",
    "Signing in…": "Entrando…",
    "Sessions stored locally · sent as {{header}} on every request.":
      "Sessão salva localmente · enviada como {{header}} a cada requisição.",
    "Username and password are required": "Usuário e senha são obrigatórios",
    "Invalid credentials": "Credenciais inválidas",
    "Welcome, {{name}}": "Bem-vindo, {{name}}",
    "What's new": "Novidades",
    "build": "build",
    "region": "região",
    "env": "ambiente",
    "Previous highlight": "Anterior",
    "Next highlight": "Próximo",
    "new": "novo",
    "improved": "melhorado",
    "update": "atualização",
  },
};

// Detect browser language on first load, fallback to English.
function detectInitial() {
  if (typeof window === "undefined") return "en";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && LOCALES.some((l) => l.code === saved)) return saved;
  const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
  return LOCALES.some((l) => l.code === nav) ? nav : "en";
}

let currentLocale = detectInitial();
const listeners = new Set();

function emit() {
  for (const l of listeners) l();
}

export function getLocale() {
  return currentLocale;
}

export function setLocale(code) {
  if (!LOCALES.some((l) => l.code === code)) return;
  currentLocale = code;
  try { window.localStorage.setItem(STORAGE_KEY, code); } catch { /* storage full or blocked — silent */ }
  document.documentElement.lang = code;
  emit();
}

// Subscribe pattern for useSyncExternalStore.
function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Hook returning [locale, setLocale] so components re-render on change.
export function useLocale() {
  const locale = useSyncExternalStore(subscribe, getLocale, getLocale);
  return [locale, setLocale];
}

// Translate `key` for the current locale. `vars` supports {{placeholder}}
// substitution — e.g. t("Welcome, {{name}}", { name: user }).
export function t(key, vars) {
  const table = dict[currentLocale];
  const raw = (table && table[key]) || key;
  if (!vars) return raw;
  return raw.replace(/{{\s*(\w+)\s*}}/g, (_, k) => (vars[k] != null ? vars[k] : ""));
}
